import React, { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import HttpsRedirect from "react-https-redirect";
import Header from "./Layout/Header";
import Footer from "./Layout/Footer";
import SideBar from "./Layout/SideBar";
import Homepage from "./Component/Homepage";
import Login from "./View/Auth/Login";
import ProductDetail from "./View/Products/ProductDetail";
import ProductList from "./View/Products/ProductList";
import NewsList from "./View/News/NewsList";
import BacktoTop from "./Component/BacktoTop";
import HistoricalData from "./View/Others/HistoricalData";
import NewsDetail from "./View/News/NewsDetail";
import Enquiry from "./View/Others/Enquiry";
import CreateRfq from "./View/Rfq/CreateRfq";
import WatchList from "./View/Dashboard/WatchList";
import QuotationList from "./View/Rfq/QuotationList";
import ReceivedQuotationList from "./View/Rfq/ReceivedQuotationList";
import SentQuotationList from "./View/Rfq/SentQuotationList";
import Address from "./View/Dashboard/Address";
import Wallet from "./View/Dashboard/Wallet";
import Faq from "./View/Others/Faq";
import ErrorPage from "./View/Others/ErrorPage";
import OrderBook from "./View/Dashboard/Orders/OrderBook";
import OrderLedger from "./View/Dashboard/Orders/OrderLedger";
import OrderDetail from "./View/Dashboard/Orders/OrderDetail";
import Profile from "./View/Profile/Profile";
import Protected from "./View/Auth/Protected";
import EmailRequired from "./View/Auth/EmailRequired";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "pace-js/themes/red/pace-theme-minimal.css";
import ContactUs from "./View/Others/ContactUs";
import BecomeSupplier from "./View/Profile/BecomeSupplier";
import UserType from "./View/Auth/UserType";
import About from "./View/Others/About";
import QuotationDetail from "./View/Rfq/QuotationDetail";
import nonetwork from "./images/Icons/no-wifi.png";
import noserver from "./images/Icons/no-server.png";
import config from "./config";
import axios from "axios";
import CreditRequest from "./View/Dashboard/CreditRequest";
import SellerQuotationDetail from "./View/Rfq/SellerQuotationDetail";
import CommodityProduct from "./View/Commodity/CommodityProduct";
import SellerOrderDetail from "./View/Dashboard/Orders/SellerOrderDetail";
import BecomeTransporter from "./View/Profile/BecomeTransporter";
import TransporterProduct from "./View/Transporter/TransporterProduct";
import TransporterEnquiryDetail from "./View/Transporter/TransporterEnquiryDetail";
import PrivacyPolicy from "./View/Others/PrivacyPolicy";
import TermsCondition from "./View/Others/TermsCondition";
import ReturnPolicy from "./View/Others/ReturnPolicy";
import Logistics from "./View/Others/Logistics";
import PaymentMethods from "./View/Others/PaymentMethods";
import SellerOrderLedger from "./View/Dashboard/Orders/SellerOrderLedger";
import NotificationSettings from "./View/Others/NotificationSettings";
import Notifications from "./View/Others/Notifications";
import authUtils from "./utils/authUtils";
import BusinessCategoryProductList from "./View/Products/BusinessCategoryProductList";
import StaffList from "./View/Staff/StaffList";
import StaffCreate from "./View/Staff/StaffCreate";
import StaffEdit from "./View/Staff/StaffEdit";

function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoading, setIsLoading] = useState(true);
  const [handshake, setHandShake] = useState(null);
  const debounceTimer = useRef(null);
  const handshakeFetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${config.apiUrl}/handshake`);
      setHandShake(response.data.success);
    } catch (error) {
      if (error.response?.status === 429) {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(handshakeFetch, 10000);
      } else {
        setHandShake(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    if (isOnline) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        handshakeFetch();
      }, 1000);
    } else {
      setIsLoading(false);
    }

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [isOnline, handshakeFetch]);

  // Initialize notifications for already authenticated users on app startup
  useEffect(() => {
    const initForAuthenticatedUser = async () => {
      try {
        await authUtils.initializeForAuthenticatedUser();
      } catch (error) {
        console.error('Error initializing notifications for authenticated user:', error);
      }
    };

    initForAuthenticatedUser();
  }, []); // Run only once on app startup

  if (!isOnline) {
    return (
      <div className="network-error">
        <div className="network-bg"></div>
        <div className="content">
          <img src={nonetwork} alt="no network" className="img-fluid" />
          <h4>Oops, No Internet Connection</h4>
          <p>No Internet Connection Found.</p>
          <p className="text-primary">
            Make sure wifi or cellular data is turned on and then try again.
          </p>
        </div>
      </div>
    );
  } else {
    if (isLoading) {
      return (
        <div className="page_loader_container">
          <div className="page_loader"></div>
        </div>
      );
    } else {
      if (!handshake) {
        return (
          <div className="network-error">
            <div className="network-bg"></div>
            <div className="content">
              <img src={noserver} alt="no network" className="img-fluid" />
              <h4>Opps, Something Went Wrong</h4>
              <p>We apologize for the inconvenience, Please try again later.</p>
              <p className="text-primary">
                Or feel free to contact us anytime if the problem persists.
              </p>
            </div>
          </div>
        );
      } else {
        return (
          <>
            <HttpsRedirect>
              <BrowserRouter>
                <div className="page-wrapper">
                  <Header />
                  <SideBar />
                  <div className="page-wrapper-content">
                    <ToastContainer />
                    <Routes>
                      <Route path="/" element={<Homepage />} />
                      <Route exact path="/login" element={<Login />} />

                      {/**Private pages only for authenticated users */}
                      <Route
                        exact
                        path="/profile"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <Profile />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/become-supplier"
                        element={
                          <Protected>
                            <UserType allowedUser={["customer"]}>
                              <BecomeSupplier />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/become-transporter"
                        element={
                          <Protected>
                            <UserType allowedUser={["customer"]}>
                              <BecomeTransporter />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/address"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <Address />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/wallet"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <Wallet />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/credit-request"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <CreditRequest />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/credit-request/:id"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <CreditRequest />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/order-book"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <OrderBook />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/order-ledger/:id"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <OrderLedger />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/order-details/:id"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <OrderDetail />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/seller-order-ledger/:id"
                        element={
                          <Protected>
                            <UserType allowedUser={["seller"]}>
                              <EmailRequired>
                                <SellerOrderLedger />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/seller-order-detail/:id"
                        element={
                          <Protected>
                            <UserType allowedUser={["seller"]}>
                              <EmailRequired>
                                <SellerOrderDetail />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/commodity-products"
                        element={
                          <Protected>
                            <UserType allowedUser={["seller"]}>
                              <EmailRequired>
                                <CommodityProduct />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/transporter-products"
                        element={
                          <Protected>
                            <UserType allowedUser={["transporter"]}>
                              <TransporterProduct />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/transporter/enquiry"
                        element={
                          <Protected>
                            <UserType allowedUser={["transporter"]}>
                              <TransporterProduct />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/transporter/orders"
                        element={
                          <Protected>
                            <UserType allowedUser={["transporter"]}>
                              <TransporterProduct />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/transporter/products"
                        element={
                          <Protected>
                            <UserType allowedUser={["transporter"]}>
                              <TransporterProduct />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/transporter/belts"
                        element={
                          <Protected>
                            <UserType allowedUser={["transporter"]}>
                              <TransporterProduct />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/transporter/vehicles"
                        element={
                          <Protected>
                            <UserType allowedUser={["transporter"]}>
                              <TransporterProduct />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/transporter-product-enquiry-detail/:id"
                        element={
                          <Protected>
                            <UserType allowedUser={["transporter"]}>
                              <TransporterEnquiryDetail />
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/create-rfq"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <CreateRfq />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/quotation-list"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <QuotationList />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/quotation-list/received"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <ReceivedQuotationList />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/quotation-list/sent"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <SentQuotationList />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/quotation-detail/:id"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <QuotationDetail />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/seller-quotation-detail/:id"
                        element={
                          <Protected>
                            <UserType allowedUser={["seller"]}>
                              <EmailRequired>
                                <SellerQuotationDetail />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/watchlist"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <WatchList />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/product-detail/:id"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <ProductDetail />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/enquiry"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <Enquiry />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/staff-list"
                        element={
                          <Protected>
                            <UserType allowedUser={["seller", "customer"]}>
                              <EmailRequired>
                                <StaffList />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/staff/create"
                        element={
                          <Protected>
                            <UserType allowedUser={["seller", "customer"]}>
                              <EmailRequired>
                                <StaffCreate />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/staff/edit/:id"
                        element={
                          <Protected>
                            <UserType allowedUser={["seller", "customer"]}>
                              <EmailRequired>
                                <StaffEdit />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      {/**End of private pages */}

                      {/**Public pages where auth is not required */}
                      <Route exact path="/news-list" element={<NewsList />} />
                      <Route
                        exact
                        path="/news-detail/:slug"
                        element={<NewsDetail />}
                      />
                      <Route
                        exact
                        path="/product-list/:id"
                        element={<ProductList />}
                      />
                      <Route
                        exact
                        path="/business-category/:id"
                        element={<BusinessCategoryProductList />}
                      />
                      <Route
                        exact
                        path="/historical-data"
                        element={<HistoricalData />}
                      />
                      <Route exact path="/faq" element={<Faq />} />
                      <Route path="*" element={<ErrorPage />} />
                      <Route exact path="/contact-us" element={<ContactUs />} />
                      <Route exact path="/about-us" element={<About />} />
                      <Route
                        exact
                        path="/privacy-policy"
                        element={<PrivacyPolicy />}
                      />
                      <Route
                        exact
                        path="/terms-and-conditions"
                        element={<TermsCondition />}
                      />
                      <Route
                        exact
                        path="/return-policy"
                        element={<ReturnPolicy />}
                      />
                      <Route exact path="/logistics" element={<Logistics />} />
                      <Route
                        exact
                        path="/payment-methods"
                        element={<PaymentMethods />}
                      />
                      <Route
                        exact
                        path="/notifications"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <Notifications />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                      <Route
                        exact
                        path="/notification-settings"
                        element={
                          <Protected>
                            <UserType
                              allowedUser={["customer", "seller", "transporter"]}
                            >
                              <EmailRequired>
                                <NotificationSettings />
                              </EmailRequired>
                            </UserType>
                          </Protected>
                        }
                      />
                    </Routes>
                  </div>
                  <Footer />
                </div>
                <BacktoTop />
              </BrowserRouter>
            </HttpsRedirect>
          </>
        );
      }
    }
  }
}

export default App;
